import React, { useState } from "react";
import SystemQualityCheckPage from "./SystemQualityCheckPage";

/**
 * SystemCheckGate
 *
 * Drop-in wrapper for existing candidate interview routes:
 *
 *   <Route path="/candidate/ai-interview/:interviewId" element={
 *     <PrivateRoute>
 *       <SystemCheckGate>
 *         <AIInterview />
 *       </SystemCheckGate>
 *     </PrivateRoute>
 *   } />
 *
 * Behavior:
 *  - Renders `children` (the existing, unmodified interview component — i.e.
 *    its normal "Start Interview" instructions page) immediately, same as
 *    before this change.
 *  - On top of that, while the check hasn't passed yet, it overlays the
 *    System Quality Check as a centered modal dialog (SystemQualityCheckPage
 *    now renders itself as a MUI Dialog). The instructions page stays visible,
 *    dimmed behind the dialog's backdrop — matching the "modal in the middle
 *    of the start page" layout, instead of a separate full-page step.
 *  - Only after the candidate explicitly clicks "Proceed to Interview" inside
 *    that dialog does it close, handing full control back to `children`.
 *  - Does not read/write any interview status, tokens, or timers — it is purely
 *    a client-side UI gate in front of whatever route it wraps. All existing
 *    URL params, query params, and auth (PrivateRoute) continue to apply exactly
 *    as before, since this component does not alter routing.
 *  - A browser refresh simply re-shows the check (session storage keeps it from
 *    re-showing on in-app remounts within the same tab session, but never marks
 *    any interview as started/completed/terminated).
 */
export default function SystemCheckGate({ children }) {
    // Per-tab-session flag only — intentionally NOT persisted to localStorage,
    // so a genuine page refresh always re-verifies device readiness (camera/mic
    // could have changed), while React remounts within the same session don't
    // force a redundant re-check.
    const [passed, setPassed] = useState(() => {
        try {
            return sessionStorage.getItem("sqc_passed_" + window.location.pathname) === "1";
        } catch {
            return false;
        }
    });

    const handlePassed = () => {
        try {
            sessionStorage.setItem("sqc_passed_" + window.location.pathname, "1");
        } catch {}
        setPassed(true);
    };

    return (
        <>
            {children}
            {!passed && <SystemQualityCheckPage onPassed={handlePassed} />}
        </>
    );
}